import { useEffect, useState, type ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import type { SortingState } from '@tanstack/react-table';
import {
  Accordion,
  AccordionItem,
  Button,
  InlineNotification,
  Tag,
} from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { AutoComplete } from 'components/autocomplete/autocomplete';
import {
  CollapsibleFilterPanel,
  FilterPanelToggle,
} from 'components/collapsible-filter-panel';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { pagedCsvExport } from 'components/data-table/fetch-all-pages';
import { CheckboxGroupFilter } from 'components/data-table/filter/checkbox-group-filter';
import { MultiSelectFilter } from 'components/data-table/filter/multi-select-filter';
import { DynamicDateFilter } from 'components/dynamic-date-filter';
import { FormattedCount } from 'components/formatted-count';
import { Link } from 'components/link';
import { PageHeader } from 'components/page-header';
import { SummaryStatCard } from 'components/summary-stat-card/summary-stat-card';
import type { EntityBaseResponse } from 'types/api-responses';
import { formatNumber } from 'utils/number';
import { EMPTY } from 'components/vesting-documents/types';
import routes from '~/routes';
import { EntityLinks } from './party-links';
import {
  fetchWorkspaces,
  pagedLoading,
  useKeyedPaging,
  useSummary,
  useWorkspaces,
  workspacePath,
  type ListPaging,
} from './api';
import type { WorkspaceListItem, WorkspacesListResponse } from './types';
import styles from './page.module.scss';
import { targetNpvSumColumn } from 'components/target-npv';

type Row = WorkspaceListItem & Record<string, unknown>;

type DateRange = { start?: string; end?: string };

interface ListFilters {
  status: string[];
  createdByIds: string[];
  entity?: EntityBaseResponse;
  createdAt?: DateRange;
  updatedAt?: DateRange;
  // Stamped once on click so the query key holds still.
  createdSince?: { days: number; start: string };
}

const NO_FILTERS: ListFilters = { status: [], createdByIds: [] };

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'promoted', label: 'Promoted' },
];

const DATE_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'updatedAt', label: 'Updated Date' },
];

/**
 * How many filters are applied.
 * @param {ListFilters} filters The filters.
 * @return {number} The count.
 */
const countFilters = (filters: ListFilters) =>
  [
    filters.status.length,
    filters.createdByIds.length,
    filters.entity,
    filters.createdAt ?? filters.createdSince,
    filters.updatedAt,
  ].filter(Boolean).length;

/**
 * The list request's params for the filters and search text.
 * @param {ListFilters} filters The filters.
 * @param {string} text The search text.
 * @return {object} The params.
 */
const listParams = (filters: ListFilters, text: string) => ({
  status: filters.status,
  created_by_ids: filters.createdByIds.map(Number),
  ...(filters.entity && { entity_id: filters.entity.id }),
  created_at:
    filters.createdAt ??
    (filters.createdSince ? { start: filters.createdSince.start } : {}),
  updated_at: filters.updatedAt ?? {},
  ...(text && { text }),
});

interface WorkspacesFiltersProps {
  filters: ListFilters;
  onChange: (next: ListFilters) => void;
  createdByOptions: WorkspacesListResponse['createdByOptions'];
}

/**
 * The list's filter panel sections.
 * @param {WorkspacesFiltersProps} props The filters and their change handler.
 * @return {JSX.Element} The sections.
 */
function WorkspacesFilters({
  filters,
  onChange,
  createdByOptions,
}: WorkspacesFiltersProps) {
  const dateValues = DATE_OPTIONS.flatMap(({ value }) => {
    const range = filters[value as 'createdAt' | 'updatedAt'];
    return range ? [{ type: value, ...range }] : [];
  });
  return (
    <Accordion className={styles.filterAccordion}>
      <AccordionItem title="Status" open>
        <CheckboxGroupFilter
          id="tsw-status-filter"
          value={filters.status}
          onChange={(status) => onChange({ ...filters, status })}
          options={STATUS_OPTIONS}
        />
      </AccordionItem>
      <AccordionItem title="Date Filters" open>
        <DynamicDateFilter
          variant="multi-select"
          values={dateValues.length ? dateValues : undefined}
          dateOptions={DATE_OPTIONS}
          onChange={(values) => {
            /**
             * The chosen range for a date type, or none when it is empty.
             * @param {string} type The date type.
             * @return {DateRange | undefined} The range.
             */
            const range = (type: string) => {
              const found = values?.find((v) => v.type === type);
              return found?.start || found?.end
                ? { start: found.start, end: found.end }
                : undefined;
            };
            const createdAt = range('createdAt');
            onChange({
              ...filters,
              createdAt,
              updatedAt: range('updatedAt'),
              createdSince: createdAt ? undefined : filters.createdSince,
            });
          }}
        />
      </AccordionItem>
      <AccordionItem title="Details" open>
        <div className={styles.filterFields}>
          <MultiSelectFilter
            autoAlign
            id="tsw-created-by-filter"
            label="Created By"
            value={filters.createdByIds}
            onChange={(createdByIds) => onChange({ ...filters, createdByIds })}
            options={(createdByOptions ?? []).map((user) => ({
              value: user.id,
              label: user.value,
            }))}
          />
          <AutoComplete<EntityBaseResponse>
            id="tsw-entity-filter"
            searchOnFocus
            // The filter panel is its own scroll box; a menu that opens downward would be clipped at its foot.
            autoAlign
            titleText="Entity"
            placeholder="Select..."
            queryResource="entities"
            status="saved"
            selectedItem={filters.entity}
            selectedItemToString={(entity) => entity?.name ?? ''}
            onChange={({ selectedItem }) =>
              onChange({ ...filters, entity: selectedItem ?? undefined })
            }
          />
        </div>
      </AccordionItem>
    </Accordion>
  );
}

/**
 * Who promoted a workspace: the user, the system, or an em dash for a draft.
 * @param {Row} row The workspace.
 * @return {string} The promoter.
 */
const promotedBy = (row: Row) =>
  row.promotedBy ?? (row.promoted ? 'System' : EMPTY);

// The largest page the server serves (Query::Workspaces::MAX_PAGE_SIZE).
const EXPORT_PAGE_SIZE = 200;

/**
 * The CSV export over every page of the list.
 * @param {object} paging The list's scope.
 * @return {object} The export config.
 */
const workspacesExport = (paging: Omit<ListPaging, 'page' | 'pageSize'>) =>
  pagedCsvExport(
    'title_support_workspaces',
    (page, pageSize, signal) =>
      fetchWorkspaces({ ...paging, page, pageSize }, signal),
    EXPORT_PAGE_SIZE
  );

const COLUMNS: DataTableColumnDef<Row>[] = [
  {
    accessorKey: 'name',
    header: 'Workspace',
    size: 320,
    cell: ({ row }) => (
      <Link to={workspacePath(row.original.id)}>{row.original.name}</Link>
    ),
  },
  {
    accessorKey: 'tractsCount',
    header: 'Tracts',
    size: 100,
    cell: ({ row }) => <FormattedCount value={row.original.tractsCount ?? 0} />,
  },
  {
    accessorKey: 'vestingDocumentsCount',
    header: 'Vesting Documents',
    size: 160,
    cell: ({ row }) => (
      <FormattedCount value={row.original.vestingDocumentsCount ?? 0} />
    ),
  },
  {
    accessorKey: 'targetNpvIncluded',
    size: 200,
    ...targetNpvSumColumn<Row>((row) => [
      row.targetNpvIncluded ?? null,
      row.targetNpvValuedCount ?? 0,
      row.tractsCount ?? 0,
    ]),
  },
  {
    id: 'entities',
    accessorFn: (row) =>
      (row.entities ?? []).map((entity) => entity.name).join(', '),
    header: 'Entities',
    size: 280,
    meta: { wrap: true },
    cell: ({ row }) => <EntityLinks entities={row.original.entities ?? []} />,
  },
  {
    accessorKey: 'promoted',
    header: 'Promoted',
    size: 120,
    meta: {
      getExportValue: (row) => (row.promoted ? 'Promoted' : 'Draft'),
    },
    cell: ({ row }) => (
      <Tag type={row.original.promoted ? 'green' : 'gray'}>
        {row.original.promoted ? 'Promoted' : 'Draft'}
      </Tag>
    ),
  },
  {
    accessorKey: 'promotedBy',
    header: 'Promoted By',
    size: 160,
    meta: { getExportValue: (row) => promotedBy(row) },
    cell: ({ row }) => promotedBy(row.original),
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    size: 160,
    cell: ({ row }) => row.original.createdBy ?? EMPTY,
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    size: 130,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Last Updated',
    size: 130,
    meta: { type: 'date' },
  },
];

interface TitleSupportWorkspacesTableProps {
  entityId?: number;
  filters?: Record<string, unknown>;
  toolbarAction?: ReactNode;
  onSearchChange?: (text: string) => void;
  onResponse?: (response: WorkspacesListResponse | undefined) => void;
}

/**
 * The workspaces table, server-paged and sorted, optionally scoped to an
 * entity.
 * @param {TitleSupportWorkspacesTableProps} props The table.
 * @return {JSX.Element} The table.
 */
export function TitleSupportWorkspacesTable({
  entityId,
  filters,
  toolbarAction,
  onSearchChange,
  onResponse,
}: TitleSupportWorkspacesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  // Embedded without a search handler (tract page, entity tab), the table searches on its own.
  const [ownText, setOwnText] = useState('');
  const query =
    onSearchChange || !ownText ? filters : { ...filters, text: ownText };
  const { page, pageSize, firstPage, onPaginationChange } = useKeyedPaging(
    JSON.stringify(query)
  );
  const scope = { entityId, filters: query, sort: sorting[0] };
  const workspaces = useWorkspaces({ page, pageSize, ...scope });
  const { data } = workspaces;
  useEffect(() => onResponse?.(data), [data, onResponse]);

  if (workspaces.isError) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Error"
        subtitle="Could not load the title support workspaces."
      />
    );
  }

  return (
    <DataTable<Row>
      data={(workspaces.data?.results ?? []) as Row[]}
      columns={COLUMNS}
      getRowId={(row) => String(row.id)}
      isLoading={pagedLoading(workspaces)}
      persistenceKey="title-support-workspaces"
      config={{
        columnFilters: false,
        sizeToContent: true,
        export: workspacesExport(scope),
      }}
      controlledState={{ pagination: { page, pageSize }, sorting }}
      onSortChange={(state) => {
        setSorting(state.sorting);
        firstPage();
      }}
      leadingToolbarActions={toolbarAction ? [toolbarAction] : undefined}
      onGlobalSearchChange={(state) =>
        (onSearchChange ?? setOwnText)(state.globalFilter)
      }
      scroll={{
        mode: 'paginated',
        serverDriven: true,
        totalCount: workspaces.data?.totalCount ?? 0,
      }}
      onPaginationChange={onPaginationChange}
      searchPlaceholder="Search..."
    />
  );
}

/**
 * The title support workspaces list page.
 * @return {JSX.Element} The page.
 */
export function TitleSupportWorkspacesPage() {
  const history = useHistory();
  const summary = useSummary();
  const [filters, setFilters] = useState<ListFilters>(NO_FILTERS);
  const [text, setText] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [response, setResponse] = useState<WorkspacesListResponse>();
  const filterCount = countFilters(filters);
  /** Shows or hides the filter panel. */
  const toggleFilters = () => setFilterOpen(!filterOpen);

  /**
   * A summary tile's created-since filter: whether it is applied, and its
   * toggle.
   * @param {number} days The window in days.
   * @return {object} Whether it is applied, and its toggle.
   */
  const createdSince = (days: number) => ({
    applied: filters.createdSince?.days === days,
    toggle: () =>
      setFilters((current) => ({
        ...current,
        createdAt: undefined,
        createdSince:
          current.createdSince?.days === days
            ? undefined
            : {
                days,
                start: moment().subtract(days, 'days').toISOString(),
              },
      })),
  });
  const promotedOnly =
    filters.status.length === 1 && filters.status[0] === 'promoted';

  const tiles: {
    title: string;
    value: number | undefined;
    tooltipText: string;
    applied?: boolean;
    toggle?: () => void;
  }[] = [
    {
      title: 'Total',
      value: summary.data?.total,
      tooltipText: 'Every title support workspace',
    },
    {
      title: 'Last 24 Hours',
      value: summary.data?.last24Hours,
      tooltipText: 'Workspaces created in the last 24 hours',
      ...createdSince(1),
    },
    {
      title: 'Last 7 Days',
      value: summary.data?.last7Days,
      tooltipText: 'Workspaces created in the last 7 days',
      ...createdSince(7),
    },
    {
      title: 'Promoted Workspaces',
      value: summary.data?.promoted,
      tooltipText: 'Workspaces promoted and frozen',
      applied: promotedOnly,
      toggle: () =>
        setFilters((current) => ({
          ...current,
          status: promotedOnly ? [] : ['promoted'],
        })),
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        breadcrumbs={[]}
        title="Title Support Workspaces"
        actions={
          <Button
            renderIcon={Add}
            size="md"
            onClick={() => history.push(routes.titleSupportWorkspaceNewPath)}
          >
            Create Workspace
          </Button>
        }
      />
      <div className={styles.cardsContainer}>
        {tiles.map((tile) => (
          <div key={tile.title} className={styles.cardCell}>
            <SummaryStatCard
              title={tile.title}
              value={formatNumber(tile.value, 0, 0, 'decimal', true, EMPTY)}
              tooltipText={tile.tooltipText}
              onFilterClick={tile.toggle}
              filterTooltip={
                tile.toggle &&
                (tile.applied
                  ? `Clear ${tile.title} filter`
                  : `Filter by ${tile.title}`)
              }
            />
          </div>
        ))}
      </div>
      <div className={styles.pageLayout}>
        <CollapsibleFilterPanel
          filterCount={filterCount}
          onReset={() => setFilters(NO_FILTERS)}
          isOpen={filterOpen}
        >
          <WorkspacesFilters
            filters={filters}
            onChange={setFilters}
            createdByOptions={response?.createdByOptions}
          />
        </CollapsibleFilterPanel>
        <div className={styles.mainContent}>
          <TitleSupportWorkspacesTable
            filters={listParams(filters, text)}
            onSearchChange={setText}
            onResponse={setResponse}
            toolbarAction={
              <FilterPanelToggle
                key="filters"
                isOpen={filterOpen}
                filterCount={filterCount}
                onToggle={toggleFilters}
              />
            }
          />
        </div>
      </div>
    </div>
  );
}
